/* eslint no-shadow: ["error", { "allow": ["state"] }] */
/* eslint-disable no-param-reassign */

import Vue from 'vue';
import { pick, cloneDeep, uniq, uniqBy } from 'lodash-es';

import toast from '../../../components/toast/toast';
import sportradarModule from '../sportradar/sportradar';
import SportradarService from '../../../services/sportradar';

import Analytics from '../../../utils/analytics';

import Conduit from '../../../services/conduitEventsService';
import Productions from '../../../services/productions';
import Graphics from '../../../services/graphics';
import Pusher from '../../../services/pusher';

import Reactive from '../../../utils/reactive';

import {
  shortenPanelUrls,
  shortenGraphicUrls,
  shortenThemeUrls,
} from '../../../utils/shortener';

import {
  // Constants
  SettingsFields,

  // Helpers
  diffpatcher,
  getItemFromTimeline,
  isAlreadyQueued,
  determineSubIndex,
  validateTimelineItem,
  getPreviousItemOrPost,
  getNextItemOrPost,
  syncQueuedPost,
  isTheme,
  getSocialPosts,

  // Set/update prevProduction for tracking changes
  getChanges,
  setPrevProduction,
  updatePrevProduction,

  // Socket emitters
  updateTimeline,
  sendSettings,
} from '../../../utils/production';

import { normalizePostForEvents } from '../../../utils/helpers';

const catchify = require('catchify');

const { v1: uuidv1 } = require('uuid');

const clone = (obj) => JSON.parse(JSON.stringify(obj));

const state = {
  loading: false,
  loadingMore: false,

  duration: 0,
  videoDuration: 0,
  production: {},

  total: 0,
  productions: [],
  flaggedProductions: [],
  deletedProductions: [],

  loadedDeletedProductions: false,
  selectedProductionId: null,
  tagsForUser: [],
  clientsInDetailedProduction: null,
  saving: false,
  lastUpdated: null,

  // Track timeline items with SR game elements
  activeSportradarInGameElements: {},

  // Pagination
  page: 1,
  limit: 50,
  owner: null,
};

const actions = {
  setOwner(context, owner) {
    context.commit('SET_OWNER', owner);
  },

  /**
   * Reset all lists of productions to nothing, and reset page number to 1.
   */
  clearProductions(context) {
    context.commit('CLEAR_PRODUCTIONS');
    context.commit('SET_PAGE', 1);
  },

  clearProduction(context) {
    // Make sure to leave the socket room before clearing the production.
    // Otherwise we'll lose the ID and be stuck in the room
    vm.$socket.emit('leave', { production_id: context.state.production._id });

    context.commit('CLEAR_PRODUCTION');
  },

  setTimelineField(context, payload) {
    context.commit('SET_TIMELINE_FIELD', payload);
  },

  setTimelineTitle(context, payload) {
    context.commit('SET_TIMELINE_TITLE', payload);
  },

  /**
   * Replace playlist item at index with provided elements(s)
   */
  replacePlaylistItem(context, payload) {
    context.commit('REPLACE_PLAYLIST_ITEM', payload);
  },

  /**
   * Replace playlist item at index withing a group with provided elements(s)
   */
  replaceGroupItem(context, payload) {
    context.commit('REPLACE_GROUP_ITEM', payload);
  },

  socket_clientsInProduction({ commit }, payload) {
    commit('UPDATE_CLIENTS_IN_DETAILED_PRODUCTION', payload.clientList);
  },

  async socket_reconnect(context) {
    const { _id: productionId } = context.state.production || {};

    if (productionId) {
      // Start by refetching the production from the API
      // jic anything was missed while disconnected.
      await context.dispatch('getProduction');

      // If anything like themes, social posts, etc.
      // were added while disconnected, then we need to
      // load them into state.
      context.dispatch('getTimelineAssets');

      // Then, rejoin the socket room for the production to get live changes.
      // We do this second so the user doesn't push any more deltas while the production
      // is loading from the API.
      vm.$socket.emit('production', { production_id: productionId });

    }
  },

  socket_timeline({ state, commit, dispatch }, payload) {
    if ('preview' in payload) {
      // All we really care about is what timeline item is set to preview
      // So this will make sure preview.index and preview.sub_index are set
      commit('PREVIEW_SET_SUB_INDEX', payload);
      return;
    }

    // If user has pending changes,
    // send them before processing delta received from another client.
    const pendingDelta = getChanges(state.production);
    if (payload.delta && state.production && pendingDelta) {
      dispatch('updateTimeline');
    }

    // Merge any deltas into current state
    commit('SET_TIMELINE', payload);

    const { timeline, index, sub_index: subIndex } = state.production;

    // Sync `queuedPost` on current live item if necessary
    if (syncQueuedPost({ timeline, index, subIndex })) {
      // `queuedPost` was updated, so make sure that change is sent to other clients
      dispatch('updateTimeline');
    }

    // Load any missing timeline items
    dispatch('getTimelineAssets');
  },

  socket_timelineSettings(context, payload) {
    const mutation = payload.preview ? 'PREVIEW_TIMELINE_SETTINGS' : 'TIMELINE_SETTINGS';
    context.commit(mutation, payload);
  },

  socket_startPlayback(context) {
    context.commit('SET_VIDEO_PLAYING', true);
  },

  socket_stopPlayback(context) {
    context.commit('SET_VIDEO_PLAYING', false);
  },

  socket_playbackDuration(context, duration) {
    context.commit('SET_DURATION', duration);
  },

  socket_videoDuration(context, payload) {
    const { duration, preview } = payload;

    if (preview) {
      context.commit('SET_PREVIEW_VIDEO_DURATION', duration);;
    } else {
      context.commit('SET_VIDEO_DURATION', duration);
    }
  },

  // temp: disable so we don't show this, and react to the message from the socket
  // socket_error(context, payload) {
  //   // ignore errors from the iframes
  //   if (
  //     context.rootState.preview.credentials.screen_id === payload.screenId
  //       || context.rootState.live.screen_id === payload.screenId
  //     ) {
  //       return;
  //   }
  //
  //   toast.makeToast({
  //     icon: 'error',
  //     loaderBg: 'red',
  //     hideAfter: false,
  //     heading: payload.title,
  //     text: payload.message,
  //   });
  // },

  /**
   * Call out to other functions that are responsible for grabbing all assets
   * from the timeline and ensuring the necessary data is loaded from the API
   * and stored in their respective lookup tables.
   * Otherwise, items in the playlist could show up as "deleted".
   * Methods should not refetch data that we already have, so it should be safe to call multiple times.
   */
  getTimelineAssets({ dispatch }) {
    dispatch('getTimelineThemes');
    dispatch('getTimelinePanels');
    dispatch('getTimelineTemplates');
    dispatch('getTimelineTagboards');
    dispatch('getTimelinePosts');
    dispatch('assets/getTimelineAssets');
    dispatch('polls/getTimelinePolls');
  },

  advance(context, direction) {
    const {
      timeline,
      index,
      sub_index: subIndex,
      looped,
    } = context.state.production;

    const currentItemIdx = timeline.findIndex((i) => i.timeline_id === index);

    if (currentItemIdx === -1) {
      // Nothing is live, so there's nowhere to go
      return;
    }

    const opts = {
      timeline,
      index,
      subIndex,
      looped,
    };

    const moveTo = direction === 'previous'
      ? getPreviousItemOrPost(opts)
      : getNextItemOrPost(opts);

    // Handle updating index/sub_index
    context.commit('ADVANCE', moveTo);

    // If moving to another social graphic or theme, then update queuedPost.
    syncQueuedPost({ timeline, ...moveTo });

    // Send changes over the socket
    context.dispatch('updateTimeline', { force: true });
  },

  updateTimeline(context, payload) {
    // Ensure sub_index is set to the proper element
    context.dispatch('syncSubIndex');

    // Then send out changes to other clients
    context.commit('UPDATE_TIMELINE', { rootState: context.rootState, ...payload });
  },

  setLayouts(context, layouts) {
    context.commit('SET_LAYOUTS', { layouts, rootState: context.rootState });
  },

  addLayouts(context, payload) {
    context.commit('ADD_LAYOUTS', { ...payload, rootState: context.rootState });
  },

  setPosts(context, payload) {
    context.commit('SET_POSTS', {
      rootState: context.rootState,
      ...payload,
    });
  },

  toggleLive(context, payload) {
    const { timelineId } = payload;

    const preview = context.rootState.preview.production;
    const { production } = context.state;

    const isAlreadyLive = (production.index === timelineId);
    const event = isAlreadyLive ? 'Off' : 'Live';
    const item = getItemFromTimeline(timelineId, production.timeline);

    context.commit('SET_SUB_INDEX', {
      index: isAlreadyLive ? null : timelineId,
      sub_index: isAlreadyLive ? null : determineSubIndex(item),
    });

    // If nothing is live anymore...
    if (isAlreadyLive) {
      // Pause any posts that were playing
      context.dispatch('stopPostVideo');

      // Pause auto-advance
      context.dispatch('pauseTimeline');
    } else if (preview.index === timelineId && preview.videoPlaying !== production.videoPlaying) {
      // If preview is already playing,
      // then copy videoPlaying flag from there to keep video state consistent.
      context.dispatch('setVideoPlaying', preview.videoPlaying);
    }

    context.dispatch('updateTimeline');

    Analytics.toggleLiveEvent({
      event,
      item,
      rootState: context.rootState,
      rootGetters: context.rootGetters,
    });
  },

  /**
   * Force sub_index to sync with current live element
   */
  syncSubIndex(context) {
    const {
      index,
      timeline,
      playing,
    } = context.state.production;

    // Don't sync anything while timeline is playing
    // Otherwise queuing a theme post in one timeline item might cause the live one to update too.
    if (playing) { return; }

    if (!index || index === '0') {
      // Reset both
      context.commit('SET_SUB_INDEX', {
        index: null,
        sub_index: null,
      });

      return;
    }

    const item = getItemFromTimeline(index, timeline);
    context.commit('SET_SUB_INDEX', {
      index,
      sub_index: determineSubIndex(item),
    });
  },

  handleTagboardDrop(context, { e, item }) {
    const { user } = context.rootState;
    const { isStaff } = context.rootGetters;
    const { production } = context.state;
    const el = e.added.element;

    Conduit.track(
      'producer.dragged.content-from-tagboard',
      {
        production_id: production?._id,
        content_type: el?.network,
        source_tagboard: el?.from_tagboard,
        destination_theme: item?.layout,
        newIndex: e?.added?.newIndex,
        contentAdded: el,
        contentDestination: item,
      },
      {
        ownerId: production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  trackLivePostEvent(context, { item, eventSource, value}) {
    const { rootState, rootGetters } = context;
    const { user } = rootState;
    const { isStaff } = rootGetters;

  const {
    _id: productionId,
    sub_index: subIndex,
    owner,
  } = context.state.production;
    // if type is element(meaning it comes from clicking Live button) then it's a new post taken live
    const isPostAlreadyLive = (subIndex === item.timeline_id) && (eventSource !== 'element');

    const post = rootState.posts.lookupTable[item.post_id];
    const {
      connectedScreens,
    } = context?.rootState?.screens || {};
    const {
      connectedScreensCount,
      hasConnectedScreens,
    } = context.rootGetters;

    Conduit.track(
      'producer.toggled.live-post',
      {
        value: value || (isPostAlreadyLive ? 'off' : 'on'),
        production_id: productionId,
        post_id: item.post_id,
        network: item.network,
        permalink: post?.permalink,
        eventSource,
        post: normalizePostForEvents(post),
        hasConnectedScreens,
        connectedScreensCount,
        connectedScreens,
      },
      {
        ownerId: owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  toggleLivePost(context, payload) {
    const { parentTimelineId, item, eventSource } = payload;

    const {
      index,
      sub_index: subIndex,
      timeline,
    } = context.state.production;

    const postTimelineId = item.timeline_id;

    const isLayoutLive = (index === parentTimelineId);
    const isQueued = isAlreadyQueued(parentTimelineId, postTimelineId, timeline);

    if (isLayoutLive) {
      const isPostAlreadyLive = (subIndex === postTimelineId);

      context.commit('SET_SUB_INDEX', {
        index: parentTimelineId,
        sub_index: isPostAlreadyLive ? null : postTimelineId,
      });

      if (eventSource !== 'tagboard') {
        // we don't care about tracking auto-advanced posts within a tagboard multipost.
        context.dispatch('trackLivePostEvent', { item, eventSource, value: isPostAlreadyLive ? 'off' : 'on' });
      }
    }

    // Sync sub_index with queuedPost field on theme.
    // If post is already queued, then unqueue it.
    syncQueuedPost({
      timeline,
      index: parentTimelineId,
      subIndex: isQueued ? null : postTimelineId,
    });

    // Trigger updateTimeline if something above changed the state.
    if (isLayoutLive || !isQueued) {
      context.dispatch('updateTimeline', { force: true });
    }
  },

  toggleLiveVideo(context) {
    const { production } = context.state;

    if (production.videoPlaying) {
      context.commit('SET_VIDEO_PLAYING', false);
    } else {
      context.commit('SET_VIDEO_PLAYING', true);
    }

    context.dispatch('sendSettings');
  },

  playPostVideo(context) {
    if (context.state.production.videoPlaying) { return; }

    context.commit('SET_VIDEO_PLAYING', true);
    context.dispatch('updateTimeline');
  },

  stopPostVideo(context) {
    if (!context.state.production.videoPlaying) { return; }

    context.commit('SET_VIDEO_PLAYING', false);
    context.dispatch('updateTimeline');
  },

  setVideoPlaying(context, videoPlaying) {
    context.commit('SET_VIDEO_PLAYING', videoPlaying);
    context.dispatch('updateTimeline');
  },

  playTimeline(context) {
    const {user} = context.rootState;
    const { isStaff } = context.rootGetters;

    Conduit.track(
      'producer.played.playlist',
      { production_id: context.state.production._id },
      {
        ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );

    const {
      index,
      sub_index: subIndex,
      timeline,
    } = context.state.production;

    const item = timeline.find((it) => it.timeline_id === index);

    // If currently on a theme without a queued post,
    // then queue the first post in the list
    if (isTheme(item?.layout) && item.posts?.length) {
      // Check that queued post exists
      const hasQueuedPost = item.queuedPost && item.posts.find((p) => p?.timeline_id === item.queuedPost);

      // If there's no queued post, or it was deleted,
      // then set queuedPost back to first post in list.
      if (!hasQueuedPost) {
        const [post] = item.posts;

        syncQueuedPost({
          timeline,
          index,
          subIndex: post?.timeline_id,
        });

        context.dispatch('updateTimeline');
      }
    }

    const isGraphic = ['graphic', 'graphics'].includes(item?.layout);

    // Auto-select first post in social graphic if nothing is already queued
    // `index` will match `sub_index` if no nested posts are queued
    if (isGraphic && (!subIndex || index === subIndex)) {
      const posts = getSocialPosts(item);

      if (posts?.length) {
        const [post] = posts;

        // Make sure the `queuedPost` field is updated on the live graphic
        syncQueuedPost({
          timeline,
          index,
          subIndex: post?.timeline_id,
        });

        context.dispatch('updateTimeline');
      }
    }

    context.commit('PLAY_TIMELINE', { rootState: context.rootState });

    // Also make sure videoPlaying flags are enabled
    context.dispatch('playPostVideo');
    context.dispatch('playPreviewVideo');

    context.dispatch('sendSettings');
  },

  pauseTimeline(context) {
    if (!context.state.production.playing) { return; }

    const {user} = context.rootState;
    const {isStaff} = context.rootGetters;

    Conduit.track(
      'producer.paused.playlist',
      { production_id: context.state.production._id },
      {
        ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );

    context.commit('PAUSE_TIMELINE', { rootState: context.rootState });

    // Also make sure videoPlaying flags are disabled
    context.dispatch('stopPostVideo');
    context.dispatch('stopPreviewVideo');

    context.dispatch('sendSettings');
  },

  togglePostVideo(context) {
    context.commit('TOGGLE_VIDEO_PLAYING');
    context.dispatch('sendSettings');
  },

  sendSettings(context) {
    sendSettings(context.state, context.rootState);

    // Since we aren't watching the production anymore,
    // settings changes need to be saved manually
    context.dispatch('updateProduction', {
      id: context.state.production._id,
      payload: pick(context.state.production, SettingsFields),
    });
  },

  removeTimelineItem(context, { timelineId }) {
    const { index, timeline } = context.state.production;

    const activeSportradarInGameTimelineIds = Object.keys(context.state.activeSportradarInGameElements);
    timeline.forEach((item) => {
      if (item.layout === 'graphics' && item.timeline_id === timelineId) { // Graphic group being removed
        const { posts } = item;
        posts.forEach((post) => {
          if (activeSportradarInGameTimelineIds.includes(post.timeline_id)) {
            context.dispatch('removeActiveSportradarInGameElements', post.timeline_id);
          }
        });
      }
    });

    if (activeSportradarInGameTimelineIds.includes(timelineId)) {
      context.dispatch('removeActiveSportradarInGameElements', timelineId);
    }

    context.commit('REMOVE_LAYOUT', { timelineId });

    context.dispatch('updateTimeline');
    context.dispatch('updatePreviewTimeline');

    if (!timeline.length || index === timelineId) {
      // Make sure the timeline isn't still playing
      context.dispatch('pauseTimeline');
      context.dispatch('stopPostVideo');
    }
  },

  addGraphicsGroup({ commit, rootState }, payload) {
    commit('ADD_GRAPHICS_GROUP', { ...payload, rootState });
  },

  // Merge two graphics at respective indices into one "graphics" component
  mergeGraphics(context, payload) {
    context.commit('MERGE_GRAPHICS', payload);

    context.dispatch('updateTimeline');
    context.dispatch('updatePreviewTimeline');
  },

  duplicateTimelineItem(context, payload) {
    const tmp = cloneDeep(payload.item);
    tmp.title += ' - COPY';
    tmp.timeline_id = uuidv1();

    // If element is a group, we need to generate new timeline_ids for all children
    if (tmp.layout === 'graphics') {
      tmp.posts.forEach((i) => {
        const item = i;
        item.timeline_id = uuidv1();
      });
    }

    context.commit('DUPLICATE_LAYOUT', { rootState: context.rootState, item: payload.item, clone: tmp });
    context.dispatch('generateShortUrls', [tmp.timeline_id]);
    context.dispatch('trackSportradarInGameElement', [tmp.timeline_id]);
  },

  duplicateTimelineItemInGroup(context, payload) {
    const tmp = cloneDeep(payload.item);
    tmp.title += ' - COPY';
    tmp.timeline_id = uuidv1();
    context.commit('DUPLICATE_LAYOUT_IN_GROUP', { rootState: context.rootState, item: payload.item, clone: tmp });
    context.dispatch('generateShortUrls', [tmp.timeline_id]);
    context.dispatch('trackSportradarInGameElement', [tmp.timeline_id]);
  },

  removeThemePost(context, payload) {
    const {
      index,
      sub_index: subIndex,
      playing,
    } = context.state.production;

    // If removing post that is currently live, and we are playing,
    // then trigger an advance first
    if (index === payload.index && subIndex === payload.sub_index && playing) {
      context.dispatch('advance');
    }

    // Remove the actual post from the timeline theme
    context.commit('REMOVE_POST', payload);

    // Finally, let other clients know what we just did
    context.dispatch('updateTimeline');
  },

  clearTimeline(context) {
    const { user } = context.rootState;
    const { isStaff } = context.rootGetters;

    Conduit.track(
      'producer.cleared.playlist',
      { production_id: context.state.production._id },
      { ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );

    context.commit('CLEAR_TIMELINE', { rootState: context.rootState });
  },

  toggleMute(context) {
    const { muted } = context.state.production;
    const { user } = context.rootState;
    const { isStaff } = context.rootGetters;

    context.commit('TOGGLE_MUTE', { rootState: context.rootState });
    context.dispatch('sendSettings');

    Conduit.track(
      'producer.toggled.playlist-mute',
      {
        production_id: context.state.production._id,
        value: (muted) ? 'unmuted' : 'muted',
      },
      {
        ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  toggleLoop(context) {
    const { looped } = context.state.production;
    const { user } = context.rootState;
    const { isStaff } = context.rootGetters;

    context.commit('TOGGLE_LOOP', { rootState: context.rootState });
    context.dispatch('sendSettings');

    Conduit.track(
      'producer.toggled.playlist-loop',
      {
        production_id: context.state.production._id,
        value: (looped) ? 'disabled' : 'enabled',
        looped,
      },
      {
        ownerId: context.state.production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  startLoader(context) {
    context.commit('START_LOADER');
  },

  stopLoader(context) {
    context.commit('STOP_LOADER');
  },

  incrementPage(context) {
    context.commit('SET_PAGE', context.state.page + 1);
  },

  /**
   * Fetch list of productions (paginated).
   * @returns {Array<Object>} List of productions.
   */
  async getProductions(context) {
    const {
      page,
      limit,
    } = context.state;

    context.commit('START_LOADER');

    const data = await Productions.getProductions({ page, limit }).finally(() => {
      context.commit('STOP_LOADER');
    });

    let { productions } = data;
    if (page > 1) {
      productions = [...context.state.productions, ...data.productions];
    }

    // De-dupe jic there was a race to load more or something
    productions = uniqBy(productions, '_id');

    context.commit('SET_TOTAL_ACTIVE_PRODUCTIONS', data.total);
    context.commit('SET_PRODUCTIONS', productions);

    return data;
  },

  async starProduction(context, productionId) {
    const result = await Productions.toggleStar(productionId);

    const production = context.state.productions.find((p) => p._id === productionId);
    production.flagged = result.starred;
    context.commit('SET_PRODUCTIONS', context.state.productions.map((p) => p._id === productionId ? production : p));

    const { flaggedProductions } = context.state;
    context.commit('SET_FLAGGED_PRODUCTIONS', production.flagged ? [production, ...flaggedProductions] : flaggedProductions.filter((p) => p._id !== productionId));
  },

  async getDeletedProductions(context) {
    context.commit('START_LOADER');

    const data = await Productions.getProductions({ deleted: true }).finally(() => {
      context.commit('STOP_LOADER');
    });

    context.commit('SET_DELETED_PRODUCTIONS', data?.productions || []);
    return data;
  },

  async getStarredProductions(context) {
    const data = await Productions.getProductions({ flagged: true });
    context.commit('SET_FLAGGED_PRODUCTIONS', data?.productions || []);
    return data;
  },

  /**
   * Save the currently open production, timeline and all.
   * This will also update `lastDeltaAt`, effectively removing any pending deltas
   * waiting to be processed. This is disruptive, and will force other connected
   * users to automatically refetch the production from the API, resetting
   * any pending changes they only have on their client.
   */
  async saveProduction(context) {
    if (context.state.saving) { return; }

    const { _id: id, title } = context.state.production;

    context.commit('SET_SAVING', true);

    await Productions.updateProduction(id, context.state.production).then(() => {
      toast.makeToast({ text: `Saved "${title}" successfully!` });
    }).catch((err) => {
      console.error('Failed to save production:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: `Failed to save "${title}".`,
      });
    }).finally(() => {
      context.commit('SET_SAVING', false);
    });
  },

  async getProduction({ state, commit, dispatch }, productionId = state.production.id) {
    if (state.production?._id && state.production._id !== productionId) {
      // First leave the socket room for the previous production.
      // We don't want to receive extra messages if a user manually
      // navigates between productions in the browser.
      vm.$socket.emit('leave', { production_id: state.production.id });
    }

    // Unsubscribe from existing pusher channel, if there is one
    if (state.production?._id) {
      Pusher.getClientInstance().unsubscribe(`production_${state.production._id}`);
    }

    const production = await Productions.getProduction(productionId);
    production.id = production._id; // QoL

    commit('SET_PRODUCTION', production);

    // Listen for notifications specific to the current production.
    const channel = Pusher.getClientInstance().subscribe(`production_${state.production._id}`);

    channel.bind('save', () => {
      // Refetch the current production whenever it's manually saved,
      // regardless of who saved it.
      // We _could_ check the userId on opts, but a single user could have multiple
      // producer clients open, so unless we update the save endpoint to
      // send us a socket ID, then we don't know who saved _exactly_.
      dispatch('getProduction');
    });

    channel.bind('delta-error', (err) => {
      console.error('Delta Error:', err);

      // NOTE: We probably want to handle this a different way,
      // but manually disconnected the socket will show the warning
      // and the user will have to reload before accessing the production again.
      // This will ensure they have the latest production and no more bad
      // deltas in their local state.
      window.$socket.disconnect();
    });

    return production;
  },

  addToActiveSportradarInGameElements(context, { timelineId, sport, gameId }) {
    const { state } = context;
    const sameGameItems = [];

    // track the number of elements subscribed to the gameId
    const keys = Object.keys(state.activeSportradarInGameElements);
    keys.forEach((key) => {
      const element = state.activeSportradarInGameElements[key];
      if (element?.gameIds?.includes(gameId)) {
        sameGameItems.push(key);
      }
    });

    // Subscribe to game channel if there are no existing subscriptions for that game
    if (!sameGameItems.length) {
      SportradarService.subscribeToGameChannel(sport, gameId);
    }

    // Handle state updates
    if (state.activeSportradarInGameElements[timelineId]) { // element already exists in state
      const timelineElement = state.activeSportradarInGameElements[timelineId];
      if (timelineElement?.gameIds && !timelineElement.gameIds.includes(gameId)) {
        timelineElement.gameIds.push(gameId);
      }
    } else if (!state.activeSportradarInGameElements[timelineId]) { // element not yet in state
      state.activeSportradarInGameElements[timelineId] = {
        sport,
        gameIds: [gameId],
      };
    }
  },

  removeActiveSportradarInGameElements(context, timelineId) {
    const { state } = context;
    const { sport, gameIds: removedGameIds } = state.activeSportradarInGameElements[timelineId];

    // First remove the current element
    delete state.activeSportradarInGameElements[timelineId];

    // Check if any other active SR game elements are subscribing to the same sport/gameId
    removedGameIds.forEach((removedGameId) => {
      const keys = Object.keys(state.activeSportradarInGameElements);
      const sameGameItems = [];
      keys.forEach((key) => {
        const timelineElement = state.activeSportradarInGameElements[key];
        if (timelineElement.gameIds.includes(removedGameId)) {
          sameGameItems.push(key);
        }
      });

      // Unsubscribe to game channel if there are no remaining subscriptions for that game
      if (!sameGameItems.length) {
        SportradarService.unsubscribeFromGameChannel(sport, removedGameId);
      }
    });
  },

  /**
   * Check if template has a SR In Game elements
   * Add to active list if so
   * * @param {Array<int>} [timelineIds]
   */
  async trackSportradarInGameElement(context, timelineIds) {
    const { state } = context;
    const { production } = state;
    const { timeline } = production;

    if (!timeline) { return; }

    // Get list of templateIds from timeline that contain a sportradar element.
    const items = timeline.reduce((memo, item) => {
      if (item.layout === 'graphic' && item.meta?.elementCounts?.sportradar) {
        memo.set(item.timeline_id, item.template_id);
      } else if (item.layout === 'graphics') {
        item.posts.forEach((graphic) => {
          if (graphic?.meta?.elementCounts?.sportradar) {
            memo.set(graphic.timeline_id, graphic.template_id);
          }
        });
      }

      return memo;
    }, new Map());

    const templateIds = uniq([...items.values()]);
    if (!templateIds.length) { return; }

    const templates = await Graphics.getMultipleTemplates(templateIds).catch((err) => {
      console.error('Failed to fetch graphic templates for sportradar tracking:', err);
      return [];
    });

    const srGameElements = templates.reduce((results, template) => {
      const { elements, _id: templateId } = template;

      elements.forEach((element) => {
        if (element.type === 'sportradar' && element.data?.gameId?.length) { // "Game" elements only
          results.push({
            templateId,
            sport: element.data.sport,
            gameId: element.data.gameId,
          });
        }
      });

      return results;
    }, []);

    srGameElements.forEach((element) => {
      const { sport, gameId } = element;

      // Grab timeline_id for items using specified template
      for (const [timelineId, templateId] of items.entries()) { // eslint-disable-line
        if (templateId === element.templateId) {
          context.dispatch('addToActiveSportradarInGameElements', {
            timelineId,
            sport,
            gameId,
          });
        }
      }
    });
  },

  /**
   * Loop over timeline items and generate short URLs
   * for any QR codes found that don't already have a short URL
   * @param {Array<int>} [timelineIds] - Limit short URL generation to provided list of items
   */
  async generateShortUrls(context, timelineIds) {
    const { state, rootState } = context;
    // Check if feature is enabled
    if (process.env.QR_CODE_SWITCH !== 'ON') { return; }

    const { production } = state;
    const { timeline: initialTimeline } = production;

    if (!initialTimeline) { return; }

    let timeline = initialTimeline;

    if (timelineIds && timelineIds.length) {
      // Only include items in list of timelineIds (and parents of the items if in a group)
      timeline = timeline.filter((item) => (
        timelineIds.includes(item.timeline_id)
          || item.posts.find((p) => timelineIds.includes(p?.timeline_id))
      ));
    }

    const opts = {
      production,
      timelineIds,
      panels: rootState.panels.lookupTable,
      templates: rootState.graphics.templatesLookupTable,
      themes: rootState.themes.lookupTable,
    };

    // Loop over every timeline item and check if there are URLs in desparate need of shortening
    await Promise.all(
      timeline.map(async (post) => {
        switch (post.layout) {
          case 'panel': {
            await shortenPanelUrls({ ...opts, post });
            break;
          }

          case 'graphics': {
            await Promise.all(
              post.posts.map((p) => shortenGraphicUrls({ ...opts, post: p })),
            );

            break;
          }

          case 'graphic': {
            await shortenGraphicUrls({ ...opts, post });
            break;
          }

          case 'grid':
          case 'waterfall':
          case 'isolated':
          case 'carousel': {
            await shortenThemeUrls({ ...opts, post });
            break;
          }

          default: break;
        }
      }),
    );
  },

  /**
   * Create a new production and return the result from the API request.
   * @param {Object} context - Store context.
   * @param {Object} production - Initial data for new production.
   * @returns {Promise<Object>} Created production.
   */
  async createProduction(context, production) {
    const data = await Productions.createProduction({
      ...production,
      owner: context.state.owner,
    });

    return data?.id;
  },

  async deleteProduction({ commit, rootState, rootGetters }, { production, trigger }) {
    const [err, data] = await catchify(
      Productions.deleteProduction(production._id),
    );

    const { user } = rootState;
    const { isStaff } = rootGetters;

    if (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete production:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: `Failed to delete ${production.title}.`,
      });

      throw err;
    }

    commit('DELETE_PRODUCTION', production);

    toast.makeToast({
      text: `${production.title} has been deleted.`,
    });

    Conduit.track(
      'producer.deleted.production',
      {
        production_id: production._id,
        tags: production.tags,
        title: production.title,
        starred: production.flagged,
        trigger,
      },
      {
        ownerId: production.owner,
        userId: user.id,
        isStaff,
      },
    );

    return data;
  },

  async restoreProduction({ commit, rootState, rootGetters }, { production, trigger }) {
    const { user}  = rootState;
    const { isStaff } = rootGetters;

    try {
      await Productions.restoreProduction(production._id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to restore production:', err);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: `Something went wrong. "${production.title}" may not have been deleted. Refresh to confirm.`,
      });

      throw err;
    }

    commit('RESTORE_PRODUCTION', production);
    toast.makeToast({ text: `Restored "${production.title}" successfully.` });

    Conduit.track(
      'producer.restored.production',
      {
        production_id: production._id,
        tags: production.tags,
        title: production.title,
        starred: production.flagged,
        trigger,
      },
      {
        ownerId: production.owner,
        userId: user.id,
        isStaff,
      },
    );
  },

  async updateProduction(context, { id, payload }) {
    await Productions.updateProduction(id, payload);

    context.commit('MERGE_PRODUCTION_EDITS', {
      edit: {
        _id: id,
        ...payload,
      },
    });

    // If current production was updated, then send changes to other clients.
    if (id === context.state.production?._id) {
      context.dispatch('updateTimeline');
    }
  },

  async updateDimensions(context, { xDimension = false, yDimension = false }) {
    context.commit('UPDATE_DIMENSIONS', { xDimension, yDimension });

    const { _id: id } = context.state.production;
    const payload = pick(context.state.production, ['xDimension', 'yDimension']);

    context.dispatch('updateProduction', { id, payload });
  },

  setBackgroundType({ commit, state, dispatch }, backgroundType) {
    commit('SET_BACKGROUND_TYPE', backgroundType);

    const { _id: id } = state.production;
    const payload = pick(state.production, ['backgroundType']);

    dispatch('updateProduction', { id, payload });
  },

  setBackgroundAsset({ commit, state, dispatch }, backgroundAsset) {
    if (state.production.backgroundType === 'image') {
      commit('SET_BACKGROUND_IMAGE', backgroundAsset);
    } else {
      commit('SET_BACKGROUND_VIDEO', backgroundAsset);
    }

    const { _id: id } = state.production;
    const payload = pick(state.production, ['backgroundType', 'backgroundImage', 'backgroundVideo']);

    dispatch('updateProduction', { id, payload });
  },

  sendAction(context, payload) {
    vm.$socket.emit('handleAction', { production_id: this.state.production.production._id, action: payload });
  },

  registerDataModule(context, payload) {
    const { element } = payload;
    const { type } = element;

    if (type === 'sportradar') {
      if (!this.hasModule(element.id)) {
        // TODO: this is the same way sr data is stored in state with graphics for consistency
        // but it's not really necessary to store everything in state per element all of the time
        // notably when two of the same sport(nba) elements.
        this.registerModule(element.id, sportradarModule);
      }
    }
  },
};

const mutations = {
  SET_PAGE(state, page) {
    state.page = page;
  },

  SET_TIMELINE_FIELD(state, payload) {
    const {
      timelineId,
      field,
      value,
    } = payload;

    const idx = state.production.timeline.findIndex((i) => i.timeline_id === timelineId);
    if (idx === -1) { return; }

    if (!state.production.timeline[idx].fields) {
      state.production.timeline[idx].fields = {};
    }

    Vue.set(state.production.timeline[idx].fields, field, value);
  },

  SET_TIMELINE_TITLE(state, payload) {
    const {
      timelineId,
      title,
    } = payload;

    const idx = state.production.timeline.findIndex((i) => i.timeline_id === timelineId);
    if (idx === -1) { return; }

    Vue.set(state.production.timeline[idx], 'title', title);
  },

  CLEAR_PRODUCTION(state) {
    // Unsubscribe from pusher channel for production notifications.
    if (state.production._id) {
      Pusher.getClientInstance().unsubscribe(`production_${state.production._id}`);
    }

    state.production = {};
  },

  CLEAR_PRODUCTIONS(state) {
    state.productions = [];
    state.flaggedProductions = [];
    state.deletedProductions = [];
  },

  REPLACE_PLAYLIST_ITEM(state, payload) {
    state.production.timeline.splice(payload.index, 1, ...payload.items);
  },

  REPLACE_GROUP_ITEM(state, payload) {
    state.production.timeline[payload.groupIndex].posts.splice(payload.index, 1, ...payload.items);
  },

  GET_TAGS_FOR_USER(state, payload) {
    state.tagsForUser = payload;
  },

  RESTORE_PRODUCTION(state, production) {
    const { _id: id } = production;

    state.productions = [production].concat(state.productions);
    state.total += 1;

    if (production.flagged) {
      state.flaggedProductions = [production].concat(state.flaggedProductions);
    }

    state.deletedProductions = state.deletedProductions.filter((p) => p._id !== id);
  },

  DELETE_PRODUCTION(state, production) {
    const { _id: id } = production;

    state.productions = state.productions.filter((p) => p._id !== id);
    state.total -= 1;
    state.flaggedProductions = state.flaggedProductions.filter((p) => p._id !== id);
    state.deletedProductions = state.deletedProductions.concat(production);
  },

  SET_PRODUCTIONS(state, payload) {
    state.productions = payload;

    // Storing this separately so we can update it client side without triggering an autosave
    state.lastUpdated = state.production.last_updated;
  },

  SET_FLAGGED_PRODUCTIONS(state, payload) {
    state.flaggedProductions = payload;
  },

  SET_DELETED_PRODUCTIONS(state, payload) {
    state.deletedProductions = payload;
    state.loadedDeletedProductions = true;
  },

  ADD_PRODUCTION(state, payload) {
    state.productions.push(payload.production);
  },

  UPDATE_CLIENTS_IN_DETAILED_PRODUCTION(state, payload) {
    state.clientsInDetailedProduction = payload;
  },

  SET_TOTAL_ACTIVE_PRODUCTIONS(state, payload) {
    state.total = payload;
  },

  SET_PRODUCTION(state, payload) {
    state.production = payload;
    state.owner = payload.owner;
    state.production.timeline = state.production.timeline.filter(validateTimelineItem);
    setPrevProduction(payload);
  },

  SET_TIMELINE(state, payload) {
    const { delta } = payload;

    // We only care if a delta is provided AND a production is still loaded in state
    if (!delta || !state.production) {
      return;
    }

    // If index or sub_index were updated in the delta, then reset video duration
    // This is so the timers properly hide when advancing from video to non-video
    if ('index' in delta || 'sub_index' in delta) {
      state.videoDuration = 0;
    }

    // Apply the delta to the current production
    try {
      diffpatcher.patch(state.production, delta);
    } catch (err) {
      console.error('Error merging delta:', delta, 'into production:', state.production);

      toast.makeToast({
        heading: 'Error',
        icon: 'error',
        loaderBg: 'red',
        text: 'Some changes to production failed to save. If this issue persists, try refreshing the production.',
      });
    }

    // Filter out invalid timeline items (null, undefined, etc.)
    state.production.timeline = state.production.timeline.filter(validateTimelineItem);

    // Ensure any new properties added to the production are reactive
    // Sadly this is still necessary, because some elements won't update properly
    Reactive.createReactiveNestedObject(state.production, state.production);

    // Store record of "previous" production for future deltas
    setPrevProduction(state.production);

    state.lastUpdated = state.production.last_updated;
  },

  UPDATE_TIMELINE(state, payload) {
    updateTimeline(state, payload.rootState, payload);
  },

  TIMELINE_SETTINGS(state, payload) {
    updatePrevProduction(payload);

    state.production = {
      ...state.production,
      ...payload,
    };
  },

  UPDATE_TIMELINE_THEME(state, payload) {
    const idx = state.production.timeline.findIndex((o) => o._id === payload.id);
    const themeIdx = payload.themes.findIndex((o) => o._id === payload.id);

    if (idx !== -1 && themeIdx !== -1) {
      Object.assign(state.production.timeline[idx], payload.themes[themeIdx]);
    }
  },

  SET_LAYOUTS(state, payload) {
    state.production.timeline = payload.layouts;
    updateTimeline(state, payload.rootState);
  },

  ADD_LAYOUTS(state, payload) {
    // Add to existing group
    if (payload.parentId) {
      const parent = state.production.timeline.find((i) => i.timeline_id === payload.parentId);
      if (!parent) {
        return;
      }

      parent.posts.push(...payload.items);
      updateTimeline(state, payload.rootState);
      return;
    }

    // Add straight to playlist
    state.production.timeline = [...state.production.timeline, ...payload.items];
    updateTimeline(state, payload.rootState);
  },

  REMOVE_LAYOUT(state, { timelineId }) {
    const parent = state.production.timeline.find((item) => (
      item.timeline_id !== timelineId
      && item.layout === 'graphics'
      && !!item.posts.find((child) => child.timeline_id === timelineId)
    ));

    // If item has a parent, just remove it from the group
    // and don't worry about checking if parent is live or not
    if (parent) {
      const index = parent.posts.findIndex((child) => child.timeline_id === timelineId);
      parent.posts.splice(index, 1);
      return;
    }

    const index = state.production.timeline.findIndex(
      (layout) => (
        layout.timeline_id === timelineId
      ),
    );

    state.production.timeline.splice(index, 1);

    // production.index is actually an UUID of the live element
    const isLayoutLive = (timelineId === state.production.index);
    state.production.index = isLayoutLive ? null : state.production.index;
    state.production.sub_index = isLayoutLive ? null : state.production.sub_index;
  },

  DUPLICATE_LAYOUT(state, payload) {
    const { timeline_id: timelineId } = payload.item;

    // Index points to the location of the element being duplicated
    // or the index of the group that element is inside of
    const index = state.production.timeline.findIndex(
      (layout) => (
        layout.timeline_id === timelineId
        || !!layout.posts.find((i) => i.timeline_id === timelineId)
      ),
    );

    if (index !== -1) {
      state.production.timeline.splice(index + 1, 0, payload.clone);
      updateTimeline(state, payload.rootState);
    }
  },

  DUPLICATE_LAYOUT_IN_GROUP(state, payload) {
    const { timeline_id: timelineId } = payload.item;

    // Find the parent of the item being duplicated
    const parent = state.production.timeline.find((item) => (
      item.timeline_id !== timelineId
      && item.layout === 'graphics'
      && !!item.posts.find((child) => child.timeline_id === timelineId)
    ));

    if (!parent) {
      return;
    }

    // Find the index of the original element inside the group
    const index = parent.posts.findIndex(
      (child) => (
        child.timeline_id === timelineId
      ),
    );

    // Finally, splice in the clone right after the nested graphic in the group
    if (index !== -1) {
      parent.posts.splice(index + 1, 0, payload.clone);
      updateTimeline(state, payload.rootState);
    }
  },

  TOGGLE_QUEUED_POST(state, payload) {
    const { timelineId, postId } = payload;
    const post = getItemFromTimeline(timelineId, state.production.timeline);

    // Only set queuedPost on the root element if it's a theme
    // Graphics keep queuedPost in the fields
    if (post && post.layout !== 'graphic') {
      post.queuedPost = postId;
    }
  },

  SET_POSTS(state, payload) {
    const idx = state.production.timeline.findIndex((i) => i.timeline_id === payload.timelineId);
    state.production.timeline[idx].posts = payload.posts;
    updateTimeline(state, payload.rootState);
  },

  REMOVE_POST(state, payload) {
    const { timeline } = state.production;

    const index = timeline.findIndex((o) => o.timeline_id === payload.index);
    const theme = timeline[index];

    // Lookup index only if provided sub_index is a timeline_id
    const postIndex = typeof payload.sub_index === 'number'
      ? payload.sub_index
      : theme.posts.findIndex((p) => p.timeline_id === payload.sub_index);

    if (payload.sub_index === state.production.sub_index) {
      state.production.sub_index = null;
    }

    theme.posts.splice(postIndex, 1);
  },

  ADD_GRAPHICS_GROUP(state, { rootState, index = -1, posts = [] }) {
    const numGroups = state.production.timeline.filter((i) => i.layout === 'graphics').length;

    const group = {
      title: `Graphics Group ${numGroups + 1}`,
      timeline_id: uuidv1(),
      displayTime: 5e3,
      transition: rootState.production.production.transition,
      layout: 'graphics',
      posts,
    };

    if (index === -1) {
      // Just append to end of timeline
      state.production.timeline.push(group);
    } else {
      // Splice into timeline at specific index
      state.production.timeline.splice(index, 0, group);
    }

    // Let connected clients know a group was added
    updateTimeline(state, rootState);
  },

  MERGE_GRAPHICS(state, { i1, i2 }) {
    const g1 = clone(state.production.timeline[i1]);
    const g2 = clone(state.production.timeline[i2]);

    const newIndex = Math.min(i1, i2);

    // Ensure elements are actually graphics or groups, in case something changed
    if (!['graphic', 'graphics'].includes(g1?.layout)) {
      return;
    }

    if (!['graphic', 'graphics'].includes(g2?.layout)) {
      return;
    }

    if ((g1.layout === 'graphics' && g2.layout === 'graphic') || (g1.layout === 'graphic' && g2.layout === 'graphics')) {
      // Add graphic to existing group, remove leftover graphic
      if (g1.layout === 'graphics') {
        // Append graphic 2 to group 1
        g1.posts.push(g2);
        state.production.timeline.splice(i1, 1, g1);
        state.production.timeline.splice(i2, 1);
      } else {
        // Prepend graphic 1 to group 2
        g2.posts.unshift(g1);
        state.production.timeline.splice(i2, 1, g2);
        state.production.timeline.splice(i1, 1);
      }

      return;
    }

    if (g1.layout === 'graphics' && g2.layout === 'graphics') {
      // Merge two different groups together, taking group 1 properties
      g1.posts = g1.posts.concat(g2.posts);
      state.production.timeline.splice(i1, 1, g1);
      state.production.timeline.splice(i2, 1);
      return;
    }

    // Two single graphics, create a new group and add both
    // New group is inserted at i1
    state.production.timeline.splice(i1, 1);
    state.production.timeline.splice(i2 > i1 ? i2 - 1 : i2, 1);

    const numGroups = state.production.timeline.filter((i) => i.layout === 'graphics').length;

    const item = {
      title: `Graphics Group ${numGroups + 1}`,
      timeline_id: uuidv1(),
      owner: g1.owner,
      displayTime: g1.displayTime,
      transition: g1.transition,
      layout: 'graphics',
      posts: [g1, g2],
    };

    // Insert new component
    state.production.timeline.splice(newIndex, 0, item);
  },

  MERGE_PRODUCTION_EDITS(state, payload) {
    const { edit } = payload;

    if (state.production._id === edit._id) {
      state.production = Object.assign(state.production, edit);
    }

    const prodIdx = state.productions.findIndex((p) => p._id === edit._id);
    if (prodIdx >= 0) {
      Object.assign(state.productions[prodIdx], edit);
    }

    const flaggedIdx = state.flaggedProductions.findIndex((p) => p._id === edit._id);
    if (flaggedIdx >= 0) {
      Object.assign(state.flaggedProductions[flaggedIdx], edit);
    }
  },

  CLEAR_TIMELINE(state, payload) {
    state.production.timeline = [];
    state.production.sub_index = null;
    state.production.index = null;
    state.production.playing = false;
    state.production.videoPlaying = false;
    state.videoDuration = 0;

    updateTimeline(state, payload.rootState);
  },

  PREVIEW_UPDATE_TIMELINE(state, payload) {
    const { force } = payload;
    const preview = {
      index: payload.index,
      sub_index: payload.sub_index,
      credentials: payload.rootState.preview.credentials,
    };

    updateTimeline(state, payload.rootState, { preview, force });
  },

  SET_SUB_INDEX(state, payload) {
    const { production } = state;
    const { index, sub_index: subIndex } = payload;

    // Also clear video duration so the UI will go away
    // until the output tells us about the new video (if there is one)
    // But only if the selected item changed
    if (production.index !== index || production.sub_index !== subIndex) {
      state.videoDuration = 0;
    }

    Vue.set(state.production, 'index', index);
    Vue.set(state.production, 'sub_index', subIndex);
  },

  ADVANCE(state, payload) {
    const { index, subIndex } = payload;
    const { production } = state;

    production.index = index;
    production.sub_index = subIndex;

    // Clear videoDuration when changing active item
    state.videoDuration = 0;
  },

  PLAY_TIMELINE(state) {
    state.production.playing = true;
  },

  PAUSE_TIMELINE(state) {
    state.production.playing = false;
  },

  SET_VIDEO_PLAYING(state, videoPlaying) {
    state.production.videoPlaying = videoPlaying;
  },

  TOGGLE_VIDEO_PLAYING(state) {
    state.production.videoPlaying = !state.production.videoPlaying;
  },

  SET_DURATION(state, payload) {
    state.duration = payload;
  },

  SET_VIDEO_DURATION(state, payload) {
    state.videoDuration = payload;
  },

  TOGGLE_MUTE(state) {
    state.production.muted = !state.production.muted;
  },

  TOGGLE_LOOP(state) {
    state.production.looped = !state.production.looped;
  },

  START_LOADER(state) {
    state.loading = true;
  },

  STOP_LOADER(state) {
    state.loading = false;
  },

  SET_SAVING(state, payload) {
    state.saving = payload;
  },

  SET_OWNER(state, owner) {
    state.owner = owner;
  },

  UPDATE_DIMENSIONS(state, payload) {
    if (payload.xDimension) state.production.xDimension = payload.xDimension;
    if (payload.yDimension) state.production.yDimension = payload.yDimension;
  },

  SET_BACKGROUND_TYPE(state, backgroundType) {
    Vue.set(state.production, 'backgroundType', backgroundType);
  },

  SET_BACKGROUND_IMAGE(state, backgroundAsset) {
    Vue.set(state.production, 'backgroundImage', backgroundAsset);
  },

  SET_BACKGROUND_VIDEO(state, backgroundAsset) {
    Vue.set(state.production, 'backgroundVideo', backgroundAsset);
  },
};

const getters = {
  /* eslint-disable no-shadow */
  isSaving(state) {
    return state.saving;
  },
};

export default {
  state,
  actions,
  mutations,
  getters,
};
